import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity.
 *
 * Supports three display modes via `data-mode`:
 *   - `stepper` (default): minus button, numeric input, plus button
 *   - `hybrid-dropdown`: native <select> with values up to a threshold + "N+";
 *     selecting "N+" swaps to a numeric input. Values <= threshold on blur swap back.
 *   - `manual`: numeric input only (bulk-order)
 *
 * Quantity changes are broadcast via QuantitySelectorUpdateEvent regardless of mode.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} [minusButton]
 * @property {HTMLButtonElement} [plusButton]
 * @property {HTMLSelectElement} [dropdownSelect]
 * @property {HTMLElement} [dropdownView]
 * @property {HTMLElement} [manualView]
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;

  /**
   * Returns the current display mode.
   * @returns {'stepper' | 'hybrid-dropdown' | 'manual'}
   */
  get mode() {
    const value = this.dataset.mode;
    if (value === 'hybrid-dropdown' || value === 'manual') return value;
    return 'stepper';
  }

  /**
   * Dropdown threshold for hybrid mode — value above which the "N+" option swaps to manual.
   */
  get dropdownThreshold() {
    return parseIntOrDefault(this.dataset.dropdownThreshold, 10);
  }

  connectedCallback() {
    // Set required refs by mode before super runs ref validation.
    // (The base class declares `requiredRefs;` as a class field, so it can't be
    //  overridden by a prototype getter; assigning here works because attributes
    //  are guaranteed to be parsed by the time connectedCallback fires.)
    this.requiredRefs = this.mode === 'stepper' ? ['quantityInput', 'minusButton', 'plusButton'] : ['quantityInput'];

    super.connectedCallback();

    const { minusButton, plusButton } = this.refs;

    if (minusButton?.disabled) this.serverDisabledMinus = true;
    if (plusButton?.disabled) this.serverDisabledPlus = true;

    this.initialized = true;

    if (this.mode === 'hybrid-dropdown') {
      this.syncDropdownToInput();
    }
    this.updateButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  /**
   * Checks if the current quantity can be added to cart without exceeding max
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets the current quantity value
   * @returns {string} The current value
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Sets the current quantity value (and syncs dropdown view if applicable)
   * @param {string} value - The value to set
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
    if (this.mode === 'hybrid-dropdown') {
      this.syncDropdownToInput();
    }
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment
   * @param {string} min - Minimum value
   * @param {string|null} max - Maximum value (null if no max)
   * @param {string} step - Step increment
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;

    quantityInput.min = min;
    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = step;

    const newMin = parseIntOrDefault(min, 1);
    const newStep = parseIntOrDefault(step, 1);
    const effectiveMax = this.getEffectiveMax();

    // Snap to valid increment if not already aligned
    let newValue = currentValue;
    if ((currentValue - newMin) % newStep !== 0) {
      // Snap DOWN to closest valid increment
      newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
    }

    // Ensure value is within bounds
    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

    if (newValue !== currentValue) {
      quantityInput.value = newValue.toString();
    }

    if (this.mode === 'hybrid-dropdown') {
      this.syncDropdownToInput();
    }
    this.updateButtonStates();
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  /**
   * Gets the effective maximum value for this quantity selector
   * Product page: max - cartQuantity (how many can be added)
   * Override in subclass for different behavior
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    // Product page: can only add what's left
    return Math.max(max - cartQuantity, min);
  }

  /**
   * Updates button states based on current value and limits.
   * No-op when minus/plus buttons are not present (hybrid/manual modes).
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    if (!minusButton || !plusButton) return;

    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * Updates quantity by a given step
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event (on blur, stepper mode).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Snap to bounds
    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, parseInt(event.target.value) || 0));

    // Validate step increment
    if ((quantity - min) % step !== 0) {
      // Set the invalid value and trigger native HTML validation
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles a dropdown selection in hybrid-dropdown mode.
   * Numeric values update the input. The "__more__" option swaps to manual input.
   * @param {Event} event
   */
  handleDropdownChange(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    const { dropdownSelect, quantityInput } = this.refs;
    if (!dropdownSelect) return;

    const value = event.target.value;
    if (value === '__more__') {
      this.switchToManual();
      return;
    }

    const numValue = parseInt(value, 10);
    if (Number.isNaN(numValue)) return;

    quantityInput.value = numValue.toString();
    this.onQuantityChange();
  }

  /**
   * Switch from dropdown view to manual numeric input.
   * Defaults the value to threshold+1 if currently at/below the threshold.
   */
  switchToManual() {
    const { dropdownView, manualView, quantityInput } = this.refs;
    if (!dropdownView || !manualView) return;

    const { min, step, value } = this.getCurrentValues();
    const threshold = this.dropdownThreshold;

    // Bump the value past the threshold so manual mode starts useful.
    if (value <= threshold) {
      const stepSize = step > 0 ? step : 1;
      let target = threshold + 1;
      // Snap UP to the next valid step from min
      const offset = target - min;
      const remainder = offset % stepSize;
      if (remainder !== 0) target += stepSize - remainder;

      const effectiveMax = this.getEffectiveMax();
      target = Math.max(min, Math.min(effectiveMax ?? Infinity, target));
      quantityInput.value = target.toString();
    }

    dropdownView.hidden = true;
    manualView.hidden = false;

    // Defer focus until after the layout shift settles, otherwise iOS skips it
    requestAnimationFrame(() => {
      quantityInput.focus();
      quantityInput.select();
    });

    this.onQuantityChange();
  }

  /**
   * Switch back to dropdown view (hybrid mode), keeping the dropdown in sync with the input.
   */
  switchToDropdown() {
    const { dropdownView, manualView } = this.refs;
    if (!dropdownView || !manualView) return;

    manualView.hidden = true;
    dropdownView.hidden = false;
    this.syncDropdownToInput();
  }

  /**
   * Mirror the input's current numeric value into the dropdown selection.
   * Values above the threshold collapse to the "__more__" sentinel.
   */
  syncDropdownToInput() {
    const { dropdownSelect, quantityInput } = this.refs;
    if (!dropdownSelect) return;

    const value = parseIntOrDefault(quantityInput.value, 1);
    const threshold = this.dropdownThreshold;

    if (value > threshold) {
      // Pick __more__ if available; otherwise leave alone.
      const moreOption = dropdownSelect.querySelector('option[value="__more__"]');
      if (moreOption) dropdownSelect.value = '__more__';
    } else {
      const target = value.toString();
      const exists = dropdownSelect.querySelector(`option[value="${target}"]`);
      if (exists) dropdownSelect.value = target;
    }
  }

  /**
   * Handle blur on the manual input (hybrid/manual modes).
   * Validates, clamps, snaps to step, dispatches change, and (in hybrid)
   * swaps back to the dropdown if the value drops at/under the threshold.
   * @param {Event} event
   */
  handleManualBlur(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const raw = parseInt(event.target.value, 10);
    let value = Number.isNaN(raw) ? min : Math.min(effectiveMax ?? Infinity, Math.max(min, raw));

    const stepSize = step > 0 ? step : 1;
    if ((value - min) % stepSize !== 0) {
      value = min + Math.round((value - min) / stepSize) * stepSize;
      value = Math.min(effectiveMax ?? Infinity, Math.max(min, value));
    }

    quantityInput.value = value.toString();
    this.onQuantityChange();
    this.updateButtonStates();

    if (this.mode === 'hybrid-dropdown' && value <= this.dropdownThreshold) {
      this.switchToDropdown();
    }
  }

  /**
   * Handle Enter key in the manual input — confirm the value and blur.
   * @param {KeyboardEvent} event
   */
  handleManualKey(event) {
    if (!(event instanceof KeyboardEvent)) return;
    if (event.key !== 'Enter') return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    this.handleManualBlur({ target: quantityInput });
    quantityInput.blur();
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    if (this.mode === 'hybrid-dropdown') {
      this.syncDropdownToInput();
    }

    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Clamp value to new effective max if necessary
    const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    if (this.mode === 'hybrid-dropdown') {
      this.syncDropdownToInput();
    }
    this.updateButtonStates();
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
